import { Box, Typography } from '@mui/material';
import { feeApplies } from '../../../utils/fee-calculator.util';
import { formatMoney, parseMoney } from '../../../utils/format.util';
import { Quote } from '../../../models/quote.model';
import quoteService from '../../../services/quote.service';
import { useMemo, useState, useEffect } from 'react';
import { getStates } from '../../../services/states.service';
import { State } from '../../../models/state.model';

interface QuotePrintTemplateProps {
  quoteData: Partial<Quote>;
  /** Show the template on screen (not only when printing). */
  preview?: boolean;
}

export default function QuotePrintTemplate({ quoteData, preview = false }: QuotePrintTemplateProps) {
  const [states, setStates] = useState<State[]>([]);

  useEffect(() => {
    const loadStates = async () => {
      try {
        const statesData = await getStates();
        setStates(statesData);
      } catch (error) {
        console.error('Error loading states:', error);
      }
    };
    loadStates();
  }, []);

  const formatCurrency = (value: string | undefined): string => {
    return formatMoney(parseMoney(value));
  };

  const formatValue = (value: string | boolean | undefined): string => {
    if (value === undefined || value === null) return '';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    return String(value);
  };

  // Calculate fees using quote service if we have the necessary data
  const calculatedFees = useMemo(() => {
    if (!quoteData.titleState || !quoteData.county || !quoteData.basePrice) {
      return null;
    }

    try {
      const basePrice = parseMoney(quoteData.basePrice);
      const tradeInValue = parseMoney(quoteData.tradeInValue);
      const purchasePrice = basePrice;

      const feeStructure = quoteService.determineFeeStructure(quoteData.titleState, quoteData.county);

      if (feeStructure.type === 'none' || !feeStructure.fees) {
        return null;
      }

      const fees = feeStructure.fees;
      const taxableAmount = purchasePrice - tradeInValue;
      const salesTax = taxableAmount * fees.salesTax;

      return {
        titleFee: fees.titleFee,
        registrationFee: fees.registrationFee,
        salesTax,
        otherFees: fees.otherFees,
        feeStructure,
      };
    } catch (error) {
      console.error('Error calculating fees:', error);
      return null;
    }
  }, [quoteData.titleState, quoteData.county, quoteData.basePrice, quoteData.tradeInValue, states]);

  return (
    <Box
      sx={{
        display: preview ? 'block' : 'none',
        ...(preview && {
          backgroundColor: '#fff',
          color: '#000',
          padding: '50px',
          fontFamily: 'Helvetica, Arial, sans-serif',
          fontSize: '13px',
          lineHeight: '15px',
        }),
        '@media print': {
          display: 'block',
          backgroundColor: '#fff',
          color: '#000',
          padding: '50px',
          fontFamily: 'Helvetica, Arial, sans-serif',
          fontSize: '13px',
          lineHeight: '15px',
          pageBreakAfter: 'always',
        },
      }}
      className="quote-print-template"
    >
      <Box sx={{ mb: 3 }}>
        <img alt="CCL Logo" src="/assets/images/ccl-logo.png" style={{ width: '100px', marginBottom: '15px' }} />
        <Typography variant="body2" sx={{ mb: 2 }}>
          <strong>Date:</strong> {quoteData.date}
          {quoteData.id && (
            <>
              {' | '}
              <strong>Quote ID:</strong> {quoteData.id}
            </>
          )}
          {' | '}
          <strong>Status:</strong> {quoteData.status}
        </Typography>
      </Box>

      {/* Fee Summary Section - ATC Format */}
      <Box sx={{ mb: 3, mt: 4 }}>
        <Typography variant="h6" sx={{ mb: 2, borderBottom: '2px solid #000', pb: 1, fontWeight: 'bold' }}>
          Fee Summary
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {(() => {
            const getNumericValue = parseMoney;

            // Calculate taxable amount - include all fees that are part of taxable base
            const basePrice = getNumericValue(quoteData.basePrice);
            const tradeInValue = getNumericValue(quoteData.tradeInValue);
            const addOns = getNumericValue(quoteData.addOns);
            const extendedWarranties = getNumericValue(quoteData.extendedWarranties);
            const deliveryFees = getNumericValue(quoteData.deliveryFees);
            const taxesPaidToDealership = getNumericValue(quoteData.taxesPaidDealerState);

            // Tax base for state/county tax: (sales price + extended warranties + add-ons + delivery fees - taxes paid to dealership - trade-in value)
            const taxableBaseForTax =
              basePrice +
              extendedWarranties +
              addOns +
              deliveryFees -
              taxesPaidToDealership -
              tradeInValue;

            // Calculate state and county taxes using actual state and county fees
            // Use the state and county from the fee structure (which comes from titleState and county fields)
            const feeState = calculatedFees?.feeStructure?.state || quoteData.titleState;
            const feeCounty = calculatedFees?.feeStructure?.county || quoteData.county;

            // Find state and county data to get their tax fees
            const stateData = states.find((s) => s.abbreviation === feeState);
            const countyData = stateData?.counties?.find((c) => c.name === feeCounty);

            // Find state tax fee - look for fees that are percentage-based and could be state tax
            // Try multiple patterns: "state tax", "state", "sales tax", or first percentage fee if only one exists
            const stateTaxFee = stateData?.fees?.find((f) => {
              const name = f.name.toLowerCase();
              return (
                (name.includes('state') && name.includes('tax')) ||
                name === 'state tax' ||
                name === 'state sales tax' ||
                (name.includes('state') && f.type === 'percentage')
              );
            }) || (stateData?.fees?.filter((f) => f.type === 'percentage').length === 1
              ? stateData.fees.find((f) => f.type === 'percentage')
              : null);

            // Find county tax fee - look for fees that are percentage-based and could be county tax
            // Try multiple patterns: "county tax", "county", "sales tax", or first percentage fee if only one exists
            const countyTaxFee = countyData?.fees?.find((f) => {
              const name = f.name.toLowerCase();
              return (
                (name.includes('county') && name.includes('tax')) ||
                name === 'county tax' ||
                name === 'county sales tax' ||
                (name.includes('county') && f.type === 'percentage')
              );
            }) || (countyData?.fees?.filter((f) => f.type === 'percentage').length === 1
              ? countyData.fees.find((f) => f.type === 'percentage')
              : null);

            // Normalize percentage: backend may store 4.7 as 4.7 or as 47 (tenths of percent)
            const toDecimalRate = (cost: number): number => {
              if (cost > 15) return cost / 1000; // e.g. 47 -> 4.7% -> 0.047
              return cost / 100; // e.g. 4.7 -> 0.047, 6.25 -> 0.0625
            };

            // Calculate state tax - can be percentage or fixed (using tax base: sales price + warranties + add-ons + delivery - taxes paid to dealer - trade-in)
            let stateTax = 0;
            let stateTaxRate = 0;
            if (stateTaxFee) {
              const cost = parseFloat(stateTaxFee.cost) || 0;
              if (stateTaxFee.type === 'percentage') {
                stateTaxRate = toDecimalRate(cost);
                stateTax = taxableBaseForTax * stateTaxRate;
              } else if (stateTaxFee.type === 'fixed') {
                stateTax = cost;
                stateTaxRate = 0; // No rate for fixed amounts
              }
            }

            // Calculate county tax - can be percentage or fixed (same tax base)
            let countyTax = 0;
            let countyTaxRate = 0;
            if (countyTaxFee) {
              const cost = parseFloat(countyTaxFee.cost) || 0;
              if (countyTaxFee.type === 'percentage') {
                countyTaxRate = toDecimalRate(cost);
                countyTax = taxableBaseForTax * countyTaxRate;
              } else if (countyTaxFee.type === 'fixed') {
                countyTax = cost;
                countyTaxRate = 0; // No rate for fixed amounts
              }
            }
            
            // Debug: Log if fees are found
            if (feeState && !stateTaxFee) {
              console.log('State Tax fee not found for:', feeState, 'Available fees:', stateData?.fees);
            }
            if (feeCounty && !countyTaxFee) {
              console.log('County Tax fee not found for:', feeCounty, 'Available fees:', countyData?.fees);
            }

            // If we have taxesPaidDealerState, use that as state tax (manual override)
            const manualStateTax = getNumericValue(quoteData.taxesPaidDealerState);
            const finalStateTax = manualStateTax > 0 ? manualStateTax : stateTax;
            const finalCountyTax = countyTax;
            const finalSalesTaxSubTotal = finalStateTax + finalCountyTax;

            // Fixed items: anything with a $ (fixed) amount in State fees or County fees
            // Saved quotes use the fee snapshot taken when they were saved; otherwise evaluate the fee conditions now
            const stateFixedFees = (stateData?.fees ?? []).filter((f) => f.type === 'fixed' && feeApplies(f, quoteData));
            const countyFixedFees = (countyData?.fees ?? []).filter(
              (f) => f.type === 'fixed' && feeApplies(f, quoteData),
            );
            const fixedItemsFromStateAndCounty: { name: string; amount: number }[] = quoteData.fees
              ? quoteData.fees.filter((f) => f.type === 'fixed').map((f) => ({ name: f.name, amount: f.amount }))
              : [];
            (quoteData.fees ? [] : stateFixedFees).forEach((f) => {
              fixedItemsFromStateAndCounty.push({
                name: stateData && feeState ? `State (${feeState}): ${f.name}` : stateData ? `State: ${f.name}` : f.name,
                amount: parseFloat(f.cost) || 0,
              });
            });
            (quoteData.fees ? [] : countyFixedFees).forEach((f) => {
              fixedItemsFromStateAndCounty.push({
                name: countyData && feeCounty ? `County (${feeCounty}): ${f.name}` : countyData ? `County: ${f.name}` : f.name,
                amount: parseFloat(f.cost) || 0,
              });
            });
            const fixedFromFeesTotal = fixedItemsFromStateAndCounty.reduce((sum, item) => sum + item.amount, 0);

            // Global fixed amounts (can be wired to config later)
            const atcServiceFee = 99;
            const mailingExpense = 59;
            const localHandlingFee = 85;

            const totalFixedItems = fixedFromFeesTotal + atcServiceFee + mailingExpense + localHandlingFee;
            // Check payable = Sales Tax Sub-Total + all Fixed items
            const checkPayableTotal = finalSalesTaxSubTotal + totalFixedItems;

            // Total tax rate (state + county) as percentage for display
            const totalTaxRatePercent = (stateTaxRate + countyTaxRate) * 100;

            return (
              <>
                {/* 1. Sales Tax: state sales tax × (sales price + extended warranties + add-ons + delivery fees - taxes paid to dealership - trade-in value) */}
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                    Sales Tax:
                  </Typography>
                  <Box sx={{ pl: 2 }}>
                    <Typography variant="body2" sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 0.5 }}>
                      <span>
                        ({(stateTaxRate * 100).toFixed(4)}% × (Sales Price + Extended Warranties + Add-ons + Delivery Fees − Taxes Paid to Dealership − Trade-in Value) =
                      </span>
                      <strong>{formatCurrency(finalStateTax.toString())}</strong>
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: '11px', color: '#555', mt: 0.5, fontFamily: 'monospace' }}>
                      ({(stateTaxRate * 100).toFixed(4)}% × ({formatCurrency(basePrice.toString())} + {formatCurrency(extendedWarranties.toString())} + {formatCurrency(addOns.toString())} + {formatCurrency(deliveryFees.toString())} − {formatCurrency(taxesPaidToDealership.toString())} − {formatCurrency(tradeInValue.toString())})) = {formatCurrency(finalStateTax.toString())}
                    </Typography>
                    {taxableBaseForTax !== 0 && (
                      <Typography variant="body2" sx={{ fontSize: '11px', color: '#666', mt: 0.5 }}>
                        Tax base: {formatCurrency(taxableBaseForTax.toString())}
                      </Typography>
                    )}
                  </Box>
                </Box>

                {/* 2. County Tax: county tax × (same base) = $amount */}
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                    County Tax:
                  </Typography>
                  <Box sx={{ pl: 2 }}>
                    <Typography variant="body2" sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 0.5 }}>
                      <span>
                        ({(countyTaxRate * 100).toFixed(4)}% × (Sales Price + Extended Warranties + Add-ons + Delivery Fees − Taxes Paid to Dealership − Trade-in Value) =
                      </span>
                      <strong>{formatCurrency(finalCountyTax.toString())}</strong>
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: '11px', color: '#555', mt: 0.5, fontFamily: 'monospace' }}>
                      ({(countyTaxRate * 100).toFixed(4)}% × ({formatCurrency(basePrice.toString())} + {formatCurrency(extendedWarranties.toString())} + {formatCurrency(addOns.toString())} + {formatCurrency(deliveryFees.toString())} − {formatCurrency(taxesPaidToDealership.toString())} − {formatCurrency(tradeInValue.toString())})) = {formatCurrency(finalCountyTax.toString())}
                    </Typography>
                  </Box>
                </Box>

                {/* 3. Total Tax Rate (state + county) = X.XXXX% */}
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 0.5 }}>
                    <strong>Total Tax Rate:</strong>
                    <strong>{totalTaxRatePercent.toFixed(4)}%</strong>
                  </Typography>
                </Box>

                {/* 4. Sales Tax Sub-Total */}
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 0.5 }}>
                    <strong>Sales Tax Sub-Total:</strong>
                    <strong>{formatCurrency(finalSalesTaxSubTotal.toString())}</strong>
                  </Typography>

                  {/* Taxable Value as list under Sales Tax Sub-Total (matches equation: sales price + warranties + add-ons + delivery − taxes paid to dealer − trade-in) */}
                  <Box sx={{ mt: 1.5, pl: 2, borderLeft: '1px solid #ddd' }}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.75 }}>
                      Taxable Value:
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, mb: 0.75 }}>
                      {basePrice !== 0 && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px' }}>
                          <Typography variant="body2" component="span" sx={{ fontSize: '12px' }}>Sales Price</Typography>
                          <Typography variant="body2" component="span" sx={{ fontSize: '12px' }}>{formatCurrency(basePrice.toString())}</Typography>
                        </Box>
                      )}
                      {extendedWarranties !== 0 && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px' }}>
                          <Typography variant="body2" component="span" sx={{ fontSize: '12px' }}>Extended Warranties</Typography>
                          <Typography variant="body2" component="span" sx={{ fontSize: '12px' }}>{formatCurrency(extendedWarranties.toString())}</Typography>
                        </Box>
                      )}
                      {addOns !== 0 && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px' }}>
                          <Typography variant="body2" component="span" sx={{ fontSize: '12px' }}>Add-ons</Typography>
                          <Typography variant="body2" component="span" sx={{ fontSize: '12px' }}>{formatCurrency(addOns.toString())}</Typography>
                        </Box>
                      )}
                      {deliveryFees !== 0 && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px' }}>
                          <Typography variant="body2" component="span" sx={{ fontSize: '12px' }}>Delivery Fees</Typography>
                          <Typography variant="body2" component="span" sx={{ fontSize: '12px' }}>{formatCurrency(deliveryFees.toString())}</Typography>
                        </Box>
                      )}
                      {taxesPaidToDealership !== 0 && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px' }}>
                          <Typography variant="body2" component="span" sx={{ fontSize: '12px' }}>Taxes Paid to Dealership</Typography>
                          <Typography variant="body2" component="span" sx={{ fontSize: '12px' }}>-{formatCurrency(taxesPaidToDealership.toString())}</Typography>
                        </Box>
                      )}
                      {tradeInValue !== 0 && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px' }}>
                          <Typography variant="body2" component="span" sx={{ fontSize: '12px' }}>Trade-in Value</Typography>
                          <Typography variant="body2" component="span" sx={{ fontSize: '12px' }}>-{formatCurrency(tradeInValue.toString())}</Typography>
                        </Box>
                      )}
                    </Box>
                    <Typography variant="body2" sx={{ display: 'flex', justifyContent: 'space-between', fontWeight: 'bold', pt: 0.5, borderTop: '1px solid #ddd', alignItems: 'baseline' }}>
                      <span>Taxable Amount:</span>
                      <span>{formatCurrency(taxableBaseForTax.toString())}</span>
                    </Typography>
                  </Box>
                </Box>

                {/* 5. Fixed items: $ amounts in both State and County fees + global (ATC, Mailing, Local Handling) */}
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.75 }}>
                    Fixed Items:
                  </Typography>
                  <Box sx={{ mt: 0.75, pl: 2, borderLeft: '1px solid #ddd' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                      {fixedItemsFromStateAndCounty.map((item) => (
                        <Box key={item.name} sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px' }}>
                          <Typography variant="body2" component="span" sx={{ fontSize: '12px' }}>{item.name}</Typography>
                          <Typography variant="body2" component="span" sx={{ fontSize: '12px' }}>{formatCurrency(item.amount.toString())}</Typography>
                        </Box>
                      ))}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px' }}>
                        <Typography variant="body2" component="span" sx={{ fontSize: '12px' }}>ATC Service Fee</Typography>
                        <Typography variant="body2" component="span" sx={{ fontSize: '12px' }}>{formatCurrency(atcServiceFee.toString())}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px' }}>
                        <Typography variant="body2" component="span" sx={{ fontSize: '12px' }}>Mailing Expense</Typography>
                        <Typography variant="body2" component="span" sx={{ fontSize: '12px' }}>{formatCurrency(mailingExpense.toString())}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px' }}>
                        <Typography variant="body2" component="span" sx={{ fontSize: '12px' }}>Local Handling Fee</Typography>
                        <Typography variant="body2" component="span" sx={{ fontSize: '12px' }}>{formatCurrency(localHandlingFee.toString())}</Typography>
                      </Box>
                    </Box>
                  </Box>
                </Box>

                {/* 6. Total = Sales Tax Sub-Total + Fixed items; Check Payable to TSF */}
                <Box sx={{ mb: 1.5, mt: 2 }}>
                  <Typography variant="body2" sx={{ display: 'flex', justifyContent: 'space-between', fontWeight: 'bold', alignItems: 'baseline' }}>
                    <span>Total:</span>
                    <span>{formatCurrency(checkPayableTotal.toString())}</span>
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    py: 1.5,
                    borderTop: '2px solid #000',
                    borderBottom: '2px solid #000',
                    pageBreakInside: 'avoid',
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body1" sx={{ fontWeight: 'bold', fontSize: '16px' }}>
                      <strong>Check Payable to TSF:</strong>
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 'bold', fontSize: '16px' }}>
                      {formatCurrency(checkPayableTotal.toString())}
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ fontSize: '11px', color: '#666', mt: 0.5 }}>
                    (Sales Tax Sub-Total + Fixed items from State/County + ATC Service Fee + Mailing Expense + Local Handling Fee)
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: '11px', color: '#555', mt: 0.5, fontFamily: 'monospace' }}>
                    ({formatCurrency(finalSalesTaxSubTotal.toString())}
                    {fixedItemsFromStateAndCounty.map((item) => ` + ${formatCurrency(item.amount.toString())}`).join('')}
                    {' + '}{formatCurrency(atcServiceFee.toString())} + {formatCurrency(mailingExpense.toString())} + {formatCurrency(localHandlingFee.toString())}) = {formatCurrency(checkPayableTotal.toString())}
                  </Typography>
                </Box>
              </>
            );
          })()}
        </Box>
      </Box>

      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 1, borderBottom: '1px solid #ddd', pb: 0.5 }}>
          Quote Information
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
          <div>
            <strong>Driver Last Name:</strong> {formatValue(quoteData.driverLastName)}
          </div>
          <div>
            <strong>VIN Number:</strong> {formatValue(quoteData.vinNumber)}
          </div>
          <div>
            <strong>Service Form URL:</strong>{' '}
            {quoteData.serviceFormURL ? (
              <a href={quoteData.serviceFormURL} target="_blank" rel="noopener noreferrer">
                {quoteData.serviceFormURL}
              </a>
            ) : (
              'N/A'
            )}
          </div>
        </Box>
      </Box>

      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 1, borderBottom: '1px solid #ddd', pb: 0.5 }}>
          Selection Info
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2, mb: 2 }}>
          <div>
            <strong>State:</strong> {formatValue(quoteData.titleState)}
          </div>
          <div>
            <strong>County:</strong> {formatValue(quoteData.county)}
          </div>
          <div>
            <strong>City:</strong> {formatValue(quoteData.city)}
          </div>
          <div>
            <strong>Zip Code:</strong> {formatValue(quoteData.titleZip)}
          </div>
          <div>
            <strong>Transaction Type:</strong> {formatValue(quoteData.transactionType)}
          </div>
          <div>
            <strong>Collateral:</strong> {formatValue(quoteData.collateralType)}
          </div>
          <div>
            <strong>Outside City Limits:</strong> {formatValue(quoteData.outsideCityLimits)}
          </div>
        </Box>
      </Box>

      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 1, borderBottom: '1px solid #ddd', pb: 0.5 }}>
          Vehicle Information
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2, mb: 2 }}>
          <div>
            <strong>Year:</strong> {formatValue(quoteData.modelYear)}
          </div>
          <div>
            <strong>Make:</strong> {formatValue(quoteData.make)}
          </div>
          <div>
            <strong>Body Style:</strong> {formatValue(quoteData.bodyStyle)}
          </div>
          <div>
            <strong>Commercial Use:</strong> {formatValue(quoteData.commercialUse)}
          </div>
          <div>
            <strong>Odometer:</strong> {formatValue(quoteData.odometer)}
          </div>
          <div>
            <strong>Weight:</strong> {formatValue(quoteData.weight)} lbs
          </div>
          <div>
            <strong>Registered Weight:</strong> {formatValue(quoteData.registeredWeight)} lbs
          </div>
          <div>
            <strong>GVW:</strong> {formatValue(quoteData.gvw)} lbs
          </div>
          <div>
            <strong>GVWR:</strong> {formatValue(quoteData.gvwr)} lbs
          </div>
          <div>
            <strong>Fuel Type:</strong> {formatValue(quoteData.fuelType)}
          </div>
          <div>
            <strong>Length:</strong> {formatValue(quoteData.length)}
          </div>
          <div>
            <strong>Registration Months:</strong> {formatValue(quoteData.registrationMonths)}
          </div>
          <div>
            <strong>EV Tax Credit:</strong> {formatValue(quoteData.evTaxCredit)}
          </div>
          {quoteData.evTaxCredit === 'Apply credit to taxable amount' && (
            <div>
              <strong>Tax Credit Amount:</strong> {formatCurrency(quoteData.taxCreditAmount)}
            </div>
          )}
        </Box>
      </Box>

      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 1, borderBottom: '1px solid #ddd', pb: 0.5 }}>
          Transaction Information
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2, mb: 2 }}>
          <div>
            <strong>Active Military:</strong> {formatValue(quoteData.activeMilitary)}
          </div>
          <div>
            <strong>MCO/Title:</strong> {formatValue(quoteData.mcoTitle)}
          </div>
          <div>
            <strong>Plate Transfer:</strong> {formatValue(quoteData.plateTransfer)}
          </div>
          <div>
            <strong>Plate Number:</strong> {formatValue(quoteData.plateNumber)}
          </div>
          <div>
            <strong>Plate Expiration:</strong> {formatValue(quoteData.plateExpiration)}
          </div>
          <div>
            <strong>Plate Type:</strong> {formatValue(quoteData.plateType)}
          </div>
          <div>
            <strong>Lien:</strong> {formatValue(quoteData.lien)}
          </div>
          <div>
            <strong>Trade In:</strong> {formatValue(quoteData.tradeIn)}
          </div>
          {quoteData.tradeIn && (
            <>
              <div>
                <strong>Trade In Vehicle Type:</strong> {formatValue(quoteData.tradeInVehicleType)}
              </div>
              <div>
                <strong>Trade In Vehicle Sub Type:</strong> {formatValue(quoteData.tradeInVehicleSubType)}
              </div>
              <div>
                <strong>Trade In Model Year:</strong> {formatValue(quoteData.tradeInModelYear)}
              </div>
            </>
          )}
        </Box>
      </Box>

      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 1, borderBottom: '1px solid #ddd', pb: 0.5 }}>
          Taxable Fees Information
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2, mb: 2 }}>
          <div>
            <strong>Purchase Date:</strong> {formatValue(quoteData.purchaseDate)}
          </div>
          <div>
            <strong>Base Price:</strong> {formatCurrency(quoteData.basePrice)}
          </div>
          <div>
            <strong>Trade In Value:</strong> {formatCurrency(quoteData.tradeInValue)}
          </div>
          <div>
            <strong>Doc Fees:</strong> {formatCurrency(quoteData.docFees)}
          </div>
          <div>
            <strong>Extended Warranties:</strong> {formatCurrency(quoteData.extendedWarranties)}
          </div>
          <div>
            <strong>Add On's:</strong> {formatCurrency(quoteData.addOns)}
          </div>
          <div>
            <strong>Protection Packages:</strong> {formatCurrency(quoteData.protectionPackages)}
          </div>
          <div>
            <strong>Dealer Inventory Taxes:</strong> {formatCurrency(quoteData.dealerInventoryTaxes)}
          </div>
          <div>
            <strong>Delivery Fees:</strong> {formatCurrency(quoteData.deliveryFees)}
          </div>
          <div>
            <strong>Taxes Paid to Dealer State:</strong> {formatCurrency(quoteData.taxesPaidDealerState)}
          </div>
          <div>
            <strong>Dealer Service Fee:</strong> {formatCurrency(quoteData.dealerServiceFee)}
          </div>
        </Box>
      </Box>
    </Box>
  );
}
